import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, RefreshCw, Check, X, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";

const METHOD_STYLE = {
  EXTRACTED: "bg-emerald-100 text-emerald-800",
  DERIVED: "bg-sky-100 text-sky-800",
  USER_CONFIRMED: "bg-violet-100 text-violet-800",
  DEFAULTED: "bg-amber-100 text-amber-800",
  MISSING: "bg-red-100 text-red-800",
  CONFLICT: "bg-red-200 text-red-900",
};

const V = ({ label, v, testid }) => (
  <div className="flex items-center gap-2 py-1 border-b border-border" data-testid={testid}>
    <span className="text-xs text-muted-foreground w-40 shrink-0">{label}</span>
    <span className="text-sm font-medium">{v?.value ?? "—"}{v?.unit ? ` ${v.unit}` : ""}</span>
    <Badge className={`text-[10px] ${METHOD_STYLE[v?.method] || "bg-zinc-100"}`}>{v?.method}</Badge>
    {v?.origin && <span className="text-[10px] text-muted-foreground truncate max-w-[220px]">{v.origin}</span>}
  </div>
);

const CorrectionForm = ({ onSubmit, features }) => {
  const [action, setAction] = useState("correct_feature");
  const [targetId, setTargetId] = useState("");
  const [field, setField] = useState("quantity");
  const [value, setValue] = useState("");
  const [motivation, setMotivation] = useState("");
  const send = () => {
    if (!motivation.trim()) { toast.error("Motivazione obbligatoria"); return; }
    let body = { action, motivation, target_type: action === "correct_value" ? "parameter" : "feature" };
    if (action === "correct_value") {
      body.target_id = field;
      body.new_value = isNaN(Number(value)) ? value : Number(value);
    } else if (action === "add_feature") {
      body.new_value = { type: field, quantity: Number(value) || null };
    } else if (action === "unify_features") {
      body.target_ids = targetId.split(",").map((s) => s.trim()).filter(Boolean);
    } else {
      body.target_id = targetId;
      if (action === "correct_feature") body.new_value = { [field]: Number(value) || value };
    }
    onSubmit(body);
    setMotivation(""); setValue("");
  };
  return (
    <div className="space-y-2 border border-border p-3" data-testid="expert-correction-form">
      <div className="grid grid-cols-2 gap-2">
        <select className="border border-border text-sm p-1.5 bg-background" value={action}
          data-testid="correction-action-select" onChange={(e) => setAction(e.target.value)}>
          <option value="correct_feature">Correggi feature (qty/Ø)</option>
          <option value="remove_feature">Elimina falso positivo</option>
          <option value="add_feature">Aggiungi feature mancata</option>
          <option value="unify_features">Unifica duplicati tra viste</option>
          <option value="link_dimension">Associa quota a feature</option>
          <option value="correct_value">Correggi materiale/spessore/grezzo/quantità</option>
        </select>
        {action === "correct_value" ? (
          <select className="border border-border text-sm p-1.5 bg-background" value={field}
            data-testid="correction-field-select" onChange={(e) => setField(e.target.value)}>
            <option value="material">materiale</option>
            <option value="material_grade">grado</option>
            <option value="thickness_mm">spessore</option>
            <option value="stock">grezzo</option>
            <option value="order_quantity">quantità ordine</option>
            <option value="bend_count">numero pieghe</option>
          </select>
        ) : action === "add_feature" ? (
          <select className="border border-border text-sm p-1.5 bg-background" value={field}
            onChange={(e) => setField(e.target.value)}>
            <option value="hole">foro</option><option value="slot">asola</option>
            <option value="bend">piega</option><option value="thread">filetto</option>
          </select>
        ) : (
          <select className="border border-border text-sm p-1.5 bg-background" value={targetId}
            data-testid="correction-target-select" onChange={(e) => setTargetId(e.target.value)}>
            <option value="">— feature —</option>
            {features.map((f) => (
              <option key={f.physical_feature_id} value={f.physical_feature_id}>
                {`${f.physical_feature_id} (${f.type})`}
              </option>
            ))}
          </select>
        )}
      </div>
      {(action === "correct_feature" && (
        <select className="border border-border text-sm p-1.5 bg-background w-full" value={field}
          onChange={(e) => setField(e.target.value)}>
          <option value="quantity">quantità</option><option value="diameter_mm">diametro mm</option>
          <option value="width_mm">larghezza mm</option><option value="length_mm">lunghezza mm</option>
        </select>
      ))}
      {action !== "remove_feature" && action !== "unify_features" && (
        <Input placeholder="Nuovo valore" value={value} data-testid="correction-value-input"
          onChange={(e) => setValue(e.target.value)} />
      )}
      {action === "unify_features" && (
        <Input placeholder="ID feature separate da virgola" value={targetId}
          onChange={(e) => setTargetId(e.target.value)} />
      )}
      <Textarea placeholder="Motivazione tecnica (obbligatoria)" value={motivation}
        data-testid="correction-motivation-input" onChange={(e) => setMotivation(e.target.value)} />
      <Button size="sm" onClick={send} data-testid="correction-submit-btn">
        <Plus size={14} className="mr-1" /> Registra correzione
      </Button>
    </div>
  );
};

export default function ExpertQuote() {
  const { jobId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pdf-jobs/${jobId}/expert`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const sendCorrection = async (body) => {
    try {
      await api.post(`/pdf-jobs/${jobId}/expert/corrections`, body);
      toast.success("Correzione registrata (audit + esempio candidato)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const approve = async (section, approved) => {
    try {
      await api.post(`/pdf-jobs/${jobId}/expert/approve`, { section, approved });
      toast.success(`${section}: ${approved ? "approvato" : "respinto"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="p-8 text-sm" data-testid="expert-loading">Calcolo preventivo esperto…</div>;
  if (!data) return <div className="p-8 text-sm">Nessun dato.</div>;

  const comp = data.cmm.components[0];
  const feats = [
    ...comp.features.holes, ...comp.features.slots, ...comp.features.bends,
    ...comp.features.threads, ...comp.features.others,
  ];
  const q = data.quote;
  const blocked = q.price_status === "PRICE_BLOCKED";
  const approvals = data.approvals || {};

  return (
    <div className="p-4 space-y-4 max-w-6xl" data-testid="expert-quote-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to={`/pdf-jobs/${jobId}/verify`} className="text-sm flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="expert-back-link">
          <ArrowLeft size={14} /> Verifica
        </Link>
        <h1 className="text-lg font-semibold">Preventivo esperto — {jobId}</h1>
        <Button size="sm" variant="outline" onClick={load} data-testid="expert-reload-btn">
          <RefreshCw size={13} className="mr-1" /> Ricalcola
        </Button>
        <span className="ml-auto flex gap-2">
          {["reading", "cycle", "price"].map((s) => (
            <span key={s} className="flex items-center gap-1">
              <Badge className={approvals[s]?.approved ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}
                data-testid={`approval-badge-${s}`}>
                {s === "reading" ? "lettura" : s === "cycle" ? "ciclo" : "prezzo"}
              </Badge>
              <Button size="sm" variant="outline" className="h-6 px-1.5" title={`Approva ${s}`}
                data-testid={`approve-${s}-btn`} onClick={() => approve(s, true)}><Check size={12} /></Button>
              <Button size="sm" variant="outline" className="h-6 px-1.5" title={`Respingi ${s}`}
                data-testid={`reject-${s}-btn`} onClick={() => approve(s, false)}><X size={12} /></Button>
            </span>
          ))}
        </span>
      </div>

      <div className={`p-3 text-sm border ${blocked ? "bg-red-50 border-red-300 text-red-900" : "bg-emerald-50 border-emerald-300 text-emerald-900"}`}
        data-testid="expert-price-status">
        <strong>{blocked ? "PREZZO BLOCCATO" : "Prezzo definitivo consentito"}</strong>
        {" · profilo v"}{data.profile_version}{" · listino "}{q.price_list_version}
        {blocked && (
          <ul className="list-disc ml-5 mt-1">
            {q.blocked_reasons.map((r, i) => <li key={i} data-testid={`blocked-reason-${i}`}>{r}</li>)}
          </ul>
        )}
        {!blocked && (
          <span data-testid="unit-price"> · unitario {q.totals.unit_price_eur} € · totale {q.totals.total_price_eur} €</span>
        )}
      </div>

      <Card data-testid="expert-explanation-card">
        <CardHeader className="py-3"><CardTitle className="text-sm">Spiegazione del tecnico digitale</CardTitle></CardHeader>
        <CardContent>
          <pre className="text-xs whitespace-pre-wrap font-sans" data-testid="expert-explanation">{data.explanation}</pre>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="expert-cmm-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Modello del pezzo (CMM v{data.cmm.cmm_version} · KB v{data.cmm.knowledge_version})</CardTitle></CardHeader>
          <CardContent>
            <V label="Materiale" v={comp.material} testid="cmm-material" />
            <V label="Grado" v={comp.material_grade} testid="cmm-grade" />
            <V label="Spessore" v={comp.thickness_mm} testid="cmm-thickness" />
            <V label="Dimensioni finite" v={{ ...comp.finished_dimensions_mm, value: Array.isArray(comp.finished_dimensions_mm.value) ? comp.finished_dimensions_mm.value.join(" × ") : comp.finished_dimensions_mm.value }} testid="cmm-dims" />
            <V label="Grezzo" v={comp.stock} testid="cmm-stock" />
            <V label="Quantità ordine" v={data.cmm.job.order_quantity} testid="cmm-quantity" />
            <V label="Pieghe" v={comp.bend_count} testid="cmm-bends" />
            {data.cmm.missing_info.length > 0 && (
              <p className="text-xs text-red-700 mt-2" data-testid="cmm-missing">
                Mancanti: {data.cmm.missing_info.join(", ")}
              </p>
            )}
            {data.cmm.conflicts.length > 0 && (
              <p className="text-xs text-red-700" data-testid="cmm-conflicts">Conflitti: {data.cmm.conflicts.length}</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="expert-features-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Feature fisiche ({feats.length}) — dedup tra viste</CardTitle></CardHeader>
          <CardContent className="space-y-1 max-h-72 overflow-auto">
            {feats.map((f) => (
              <div key={f.physical_feature_id} className="flex items-center gap-2 text-xs border-b border-border py-1"
                data-testid={`feature-row-${f.physical_feature_id}`}>
                <span className="font-mono">{f.physical_feature_id}</span>
                <Badge className="bg-zinc-100 text-zinc-700 text-[10px]">{f.type}</Badge>
                <span>qty {f.quantity?.value ?? "?"}</span>
                {f.diameter_mm && <span>Ø{typeof f.diameter_mm === "object" ? f.diameter_mm.value : f.diameter_mm}</span>}
                <Badge className={`text-[10px] ${f.status === "EXPLICIT" || f.status === "USER_CONFIRMED" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{f.status}</Badge>
                {(f.view_instances || []).length > 1 && <span className="text-muted-foreground">{f.view_instances.length} viste</span>}
                <Button size="sm" variant="outline" className="h-5 px-1 ml-auto" title="Elimina falso positivo"
                  data-testid={`remove-feature-${f.physical_feature_id}`}
                  onClick={() => sendCorrection({ action: "remove_feature", target_id: f.physical_feature_id, target_type: "feature", motivation: "falso positivo eliminato dall'operatore" })}>
                  <Trash2 size={11} />
                </Button>
              </div>
            ))}
            {feats.length === 0 && <p className="text-xs text-muted-foreground">Nessuna feature.</p>}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="expert-cycle-card">
        <CardHeader className="py-3">
          <CardTitle className="text-sm">
            Ciclo produttivo (planner v{data.plan.planner_version} · profilo v{data.plan.profile_version})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2">Operazione</th><th className="pr-2">Macchina</th>
              <th className="pr-2">Formula</th><th className="pr-2">Setup</th><th className="pr-2">T/pezzo</th>
              <th className="pr-2">Costo</th><th className="pr-2">Motivazione</th><th>Stato</th>
            </tr></thead>
            <tbody>
              {data.plan.operations.map((o) => (
                <tr key={o.op_id} className="border-t border-border align-top" data-testid={`op-row-${o.op_id}`}>
                  <td className="py-1 pr-2 font-medium">{o.label_it || o.operation}</td>
                  <td className="pr-2">{o.machine || "—"}</td>
                  <td className="pr-2 max-w-[200px] text-muted-foreground">{o.formula || "—"}</td>
                  <td className="pr-2">{o.setup_time_s != null ? `${o.setup_time_s}s` : "—"}</td>
                  <td className="pr-2">{o.unit_time_s != null ? `${Math.round(o.unit_time_s)}s` : "—"}</td>
                  <td className="pr-2">{o.cost_eur != null ? `${o.cost_eur.toFixed(2)}€` : "—"}</td>
                  <td className="pr-2 max-w-[260px]">{o.motivation}</td>
                  <td className="space-x-1 whitespace-nowrap">
                    {o.demo_params_used.length > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`op-demo-${o.op_id}`}>DEMO</Badge>}
                    {o.missing_params.length > 0 && <Badge className="bg-red-100 text-red-800 text-[10px]" title={o.missing_params.join(", ")}>manca: {o.missing_params.join(", ")}</Badge>}
                    {o.requires_human_approval && <Badge className="bg-sky-100 text-sky-800 text-[10px]">approvare</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card data-testid="expert-quote-card">
        <CardHeader className="py-3"><CardTitle className="text-sm">Voci di costo (motore v{q.economic_engine_version})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground">
              <th className="py-1 pr-2">Voce</th><th className="pr-2">Formula</th>
              <th className="pr-2">Tempo</th><th className="pr-2">Importo</th><th>Note</th>
            </tr></thead>
            <tbody>
              {q.items.map((it) => (
                <tr key={it.code} className="border-t border-border align-top" data-testid={`quote-item-${it.code}`}>
                  <td className="py-1 pr-2 font-medium">{it.label}</td>
                  <td className="pr-2 max-w-[260px] text-muted-foreground">{it.formula || "—"}</td>
                  <td className="pr-2">{it.time_s != null ? `${Math.round(it.time_s)}s` : "—"}</td>
                  <td className="pr-2">{it.amount_eur != null ? `${it.amount_eur.toFixed(2)}€` : "—"}</td>
                  <td className="space-x-1">
                    {it.demo_params_used.length > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">DEMO</Badge>}
                    {it.missing_inputs.length > 0 && <Badge className="bg-red-100 text-red-800 text-[10px]">manca: {it.missing_inputs.join(", ")}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-xs space-y-0.5" data-testid="quote-totals">
            <p>Costo diretto/pezzo: <strong>{q.totals.cost_per_piece_direct_eur} €</strong> · costi lotto: {q.totals.lot_costs_eur} € {q.totals.lot_costs_amortized_per_piece_eur != null && `(→ ${q.totals.lot_costs_amortized_per_piece_eur} €/pezzo)`}</p>
            <p>Costo industriale unitario: <strong>{q.totals.industrial_unit_cost_eur ?? "—"} €</strong> · overhead {q.totals.overhead_pct}% · rischio {q.totals.technical_risk_pct}% · margine {q.totals.margin_pct}% ({q.totals.margin_source})</p>
            <p className="text-muted-foreground">{q.totals.formula_prezzo}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="expert-corrections-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Correzione del tecnico</CardTitle></CardHeader>
          <CardContent><CorrectionForm onSubmit={sendCorrection} features={feats} /></CardContent>
        </Card>
        <Card data-testid="expert-audit-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Audit correzioni ({(data.corrections || []).length})</CardTitle></CardHeader>
          <CardContent className="space-y-1 max-h-72 overflow-auto">
            {(data.corrections || []).map((c) => (
              <div key={c.correction_id} className="text-xs border-b border-border py-1" data-testid={`audit-${c.correction_id}`}>
                <span className="font-mono">{c.action}</span> {c.target_id || (c.target_ids || []).join("+")} — {c.motivation}
                <span className="text-muted-foreground"> · {c.user_email} · {c.at}</span>
              </div>
            ))}
            {(data.corrections || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna correzione registrata.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
